import { Gender, GenderType } from './constants/gender.constants';
import { InsuranceKey } from './constants/insurance.constants';
import { LanguageKey } from './constants/language.dto';
import { MinResultRequest } from './query-filter';

/**
 * The one canonical shape for what the Finder questionnaire's answers mean,
 * independent of Angular Forms or Mongo document shape. `null` always means
 * "genuinely unanswered" — distinct from `false`/`0`, which are real answers.
 */
export interface FinderAnswers {
  parentGender?: GenderType | null;
  parentAge?: number | null;
  childrenCount?: number | null;
  childrenAgeGroups?: boolean[] | null;
  isPregnant?: boolean | null;
  isVictimOfViolence?: boolean | null;
  isRefugee?: boolean | null;
  rent?: number | null;
  zip?: string | null;
  income?: number | null;
  relationship?: number | null;
  insurance?: InsuranceKey | null;
  jobRelatedSituation?: number | null;
}

export interface FinderQueryOptions {
  limit?: number;
  skip?: number;
  categoryId?: string;
  language?: LanguageKey;
}

const CHILD_AGE_GROUPS = [
  0.25, 0.75, 1.25, 1.75, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16,
  17, 18,
];

/**
 * The single, canonical transform from Finder answers to a search-results
 * query filter. Every call site (guest client, registered client, dashboard
 * server) must use this instead of its own copy — that duplication is what
 * previously let the guest and registered paths silently drift apart.
 */
export function buildResultsQueryFilter(
  answers: FinderAnswers | null | undefined,
  options: FinderQueryOptions = {},
): MinResultRequest & { includedLanguages?: string[] } {
  const query: MinResultRequest & { includedLanguages?: string[] } = {
    status: 'PUBLISHED',
    filterByDate: true,
  };
  if (options.limit != null) {
    query.limit = options.limit;
  }
  if (options.skip != null) {
    query.skip = options.skip;
  }
  if (options.categoryId != null) {
    query.category = options.categoryId as MinResultRequest['category'];
  }
  if (options.language) {
    query.includedLanguages = [options.language];
  }

  if (!answers) {
    return query;
  }

  if (answers.parentGender) {
    query.parentGender = answers.parentGender;
  }
  if (answers.parentAge != null) {
    query.parentAge = answers.parentAge;
  }

  if (answers.childrenCount != null && answers.childrenCount > 0) {
    query.childrenCount = answers.childrenCount;
    query.childrenAgeGroups = CHILD_AGE_GROUPS.filter(
      (_, i) => answers.childrenAgeGroups?.[i],
    );
  } else if (answers.childrenCount === 0) {
    query.childrenCount = 0;
    query.childrenAgeGroups = [];
  }

  const cantBePregnant = answers.parentGender === Gender.male;
  if (answers.isPregnant === true && !cantBePregnant) {
    query.isPregnant = true;
  } else if (answers.isPregnant === false || cantBePregnant) {
    query.isPregnant = false;
  }

  if (answers.isVictimOfViolence === true) {
    query.isVictimOfViolence = true;
  } else if (answers.isVictimOfViolence === false) {
    query.isVictimOfViolence = false;
  }
  if (answers.isRefugee === true) {
    query.isRefugee = true;
  } else if (answers.isRefugee === false) {
    query.isRefugee = false;
  }

  if (answers.rent != null) {
    query.rent = answers.rent;
  }
  if (answers.zip) {
    query.zip = answers.zip;
  }
  if (answers.income != null) {
    query.income = answers.income;
  }
  if (answers.relationship != null) {
    query.relationship = answers.relationship;
  }
  if (answers.insurance) {
    query.insurance = answers.insurance;
  }
  if (answers.jobRelatedSituation != null) {
    query.jobRelatedSituation = answers.jobRelatedSituation;
  }

  return query;
}
